import { useEffect, useRef } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';

export const buildingKey = (rakennus, idx) => rakennus.id_rakennus || rakennus.rakennustunnus || idx;

// Extracted from MapVisualMultipleModalWrapper's BuildingMapLayout, which
// originally only ever paired this with the map. Reused as-is there (map
// rendered alongside it) and as a standalone page-wide rail in
// KiinteistoDetails.jsx — same list, same highlight/scroll/click
// behavior, just a different place in the page.
export default function BuildingListRail({ allBuildings, highlightedKey, setHighlightedKey, onSelectBuilding, onToggleMainBuilding, maxHeight }) {
  // A marker click (or a selection elsewhere) highlights a row that may
  // be scrolled out of view — bring it into view automatically instead
  // of leaving the user to hunt for it. 'nearest' so clicking a row
  // that's already visible doesn't needlessly re-scroll the list.
  const rowRefs = useRef(new Map());
  useEffect(() => {
    if (highlightedKey == null) return;
    rowRefs.current.get(highlightedKey)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlightedKey]);

  if (!allBuildings.length) return null;

  return (
    <div className="border-end bg-white building-toc-scroll" style={{ width: '100%', maxHeight, overflowY: 'auto' }}>
      {/* Hover background goes through a CSS class, not inline — an
          inline `background` (even 'none') always wins over a :hover
          rule on the same element. Scrollbar hidden (still scrolls via
          wheel/trackpad/touch, just no visible track/thumb). */}
      <style>{`
        .map-building-row { transition: background-color 0.12s ease; }
        .map-building-row:hover { background: #f2f2f7; }
        .building-toc-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        .building-toc-scroll::-webkit-scrollbar { display: none; }
        /* App.css has a global mobile rule (.btn/.btn-sm under
           max-width:576px) that forces asymmetric !important padding,
           stretching this 20x20 circular button into an oval — this
           compound selector outranks that single-class rule regardless
           of CSS source order. */
        .building-info-btn.btn.btn-sm { width: 20px !important; height: 20px !important; padding: 0 !important; }
      `}</style>
      {allBuildings.map((rakennus, idx) => {
        const key = buildingKey(rakennus, idx);
        const active = highlightedKey === key;
        return (
          <div key={key} ref={el => { if (el) rowRefs.current.set(key, el); else rowRefs.current.delete(key); }}
            className="map-building-row d-flex align-items-center justify-content-between border-top"
            style={{ padding: '10px 14px', background: active ? '#eafbf1' : undefined, borderLeft: `3px solid ${active ? '#198754' : 'transparent'}` }}>
            {/* Plain click just highlights the marker on the map —
                opening the tiedot modal is a separate, explicit action
                (the info button), not a side effect of picking a
                building to look at on the map. */}
            <div onClick={() => setHighlightedKey(key)} role="button" tabIndex={0}
              className="d-flex flex-column flex-grow-1" style={{ cursor: 'pointer', minWidth: 0 }}>
              <span className="fw-semibold" style={{ fontSize: '0.82rem', color: active ? '#198754' : '#1c1c1e' }}>{rakennus?.rakennustunnus || '—'}</span>
              <span className="text-muted" style={{ fontSize: '0.74rem' }}>{rakennus?.rakennusluokitus || '—'}</span>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <OverlayTrigger placement="top" overlay={<Tooltip>Avaa rakennuksen tiedot</Tooltip>}>
                <button type="button" onClick={() => { setHighlightedKey(key); onSelectBuilding?.(rakennus); }}
                  className="building-info-btn btn btn-sm btn-outline-success d-flex align-items-center justify-content-center p-0"
                  style={{ width: 20, height: 20, borderRadius: '50%' }}>
                  <i className="bi bi-info-lg" style={{ fontSize: '0.62rem' }} />
                </button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Ensisijainen rakennus</Tooltip>}>
                <Form.Check
                  type="switch"
                  id={`main-switch-rail-${key}`}
                  checked={!!rakennus.isMainBuilding}
                  onChange={() => onToggleMainBuilding?.(rakennus)}
                  className="m-0"
                />
              </OverlayTrigger>
            </div>
          </div>
        );
      })}
    </div>
  );
}
